import React from 'react';
import { FileTextIcon, TruckIcon, CalendarIcon, BuildingIcon, MapPinIcon, DollarSignIcon, UserIcon } from 'lucide-react';
import { PurchaseOrder } from './ReceiptingView';
import { formatCurrency } from '../utils/currencyFormatter';
import { StatusBadge } from '../StatusBadge';
interface ReceiptHeaderProps {
  purchaseOrder: PurchaseOrder;
  locale?: string;
}
export function ReceiptHeader({
  purchaseOrder,
  locale = 'en-US'
}: ReceiptHeaderProps) {
  // Get status badge type based on PO status
  const getStatusBadgeType = (status: string) => {
    switch (status.toLowerCase()) {
      case 'approved':
      case 'confirmed':
        return 'success';
      case 'pending approval':
      case 'pending confirmation':
        return 'pending';
      case 'rejected':
      case 'cancelled':
        return 'error';
      default:
        return 'info';
    }
  };
  return <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-13050">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-13051">
        <div className="flex justify-between items-center" data-id="element-13052">
          <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-13053">
            <FileTextIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-13054" />
            Purchase Order Details
          </h3>
          <StatusBadge status={getStatusBadgeType(purchaseOrder.status)} text={purchaseOrder.status} size="md" data-id="element-13055" />
        </div>
      </div>
      <div className="px-4 py-5 sm:p-6" data-id="element-13056">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-13057">
          <div data-id="element-13058">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-13059">
              Purchase Order Information
            </h4>
            <div className="space-y-3" data-id="element-13060">
              <div className="flex items-start" data-id="element-13061">
                <div className="flex-shrink-0" data-id="element-13062">
                  <FileTextIcon className="h-5 w-5 text-gray-400" data-id="element-13063" />
                </div>
                <div className="ml-3" data-id="element-13064">
                  <p className="text-sm font-medium text-gray-900" data-id="element-13065">
                    {purchaseOrder.id}
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-13066">{purchaseOrder.name}</p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-13067">
                <div className="flex-shrink-0" data-id="element-13068">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-13069" />
                </div>
                <div className="ml-3" data-id="element-13070">
                  <p className="text-sm font-medium text-gray-900" data-id="element-13071">
                    Order Date
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-13072">{purchaseOrder.date}</p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-13073">
                <div className="flex-shrink-0" data-id="element-13074">
                  <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-13075" />
                </div>
                <div className="ml-3" data-id="element-13076">
                  <p className="text-sm font-medium text-gray-900" data-id="element-13077">
                    Total Value
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-13078">
                    {formatCurrency(purchaseOrder.total, purchaseOrder.currency, locale)}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-13079">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-13080">
              Vendor Information
            </h4>
            <div className="space-y-3" data-id="element-13081">
              <div className="flex items-start" data-id="element-13082">
                <div className="flex-shrink-0" data-id="element-13083">
                  <UserIcon className="h-5 w-5 text-gray-400" data-id="element-13084" />
                </div>
                <div className="ml-3" data-id="element-13085">
                  <p className="text-sm font-medium text-gray-900" data-id="element-13086">Vendor</p>
                  <p className="text-sm text-gray-500" data-id="element-13087">
                    {purchaseOrder.vendor}
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-13088">
                <div className="flex-shrink-0" data-id="element-13089">
                  <TruckIcon className="h-5 w-5 text-gray-400" data-id="element-13090" />
                </div>
                <div className="ml-3" data-id="element-13091">
                  <p className="text-sm font-medium text-gray-900" data-id="element-13092">
                    Delivery Status
                  </p>
                  <div className="mt-1" data-id="element-13093">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-13094">
                      Partially Delivered
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div data-id="element-13095">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-13096">
              Shipping Information
            </h4>
            <div className="space-y-3" data-id="element-13097">
              <div className="flex items-start" data-id="element-13098">
                <div className="flex-shrink-0" data-id="element-13099">
                  <BuildingIcon className="h-5 w-5 text-gray-400" data-id="element-13100" />
                </div>
                <div className="ml-3" data-id="element-13101">
                  <p className="text-sm font-medium text-gray-900" data-id="element-13102">Entity</p>
                  <p className="text-sm text-gray-500" data-id="element-13103">
                    {purchaseOrder.entity}
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-13104">
                <div className="flex-shrink-0" data-id="element-13105">
                  <MapPinIcon className="h-5 w-5 text-gray-400" data-id="element-13106" />
                </div>
                <div className="ml-3" data-id="element-13107">
                  <p className="text-sm font-medium text-gray-900" data-id="element-13108">Site</p>
                  <p className="text-sm text-gray-500" data-id="element-13109">{purchaseOrder.site}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}